import { MISSION_TEMPLATES, missionTarget, type MissionMetric, type MissionTemplate } from '@accra/shared';
import type { RunStats } from '../game/RunState';

export interface ActiveMission {
  id: string;
  target: number;
  /** Progress carried over from earlier shifts (only for 'total' missions). */
  carried: number;
  done: boolean;
}

export interface MissionBoard {
  level: number;
  missions: ActiveMission[];
}

function template(id: string): MissionTemplate {
  const found = MISSION_TEMPLATES.find((m) => m.id === id);
  if (!found) throw new Error(`unknown mission ${id}`);
  return found;
}

/** Three different missions, rotating through the list as you level up. */
export function newBoard(level: number): MissionBoard {
  const count = MISSION_TEMPLATES.length;
  const missions = [0, 1, 2].map((i) => {
    const t = MISSION_TEMPLATES[((level - 1) * 3 + i * 2) % count] ?? MISSION_TEMPLATES[i]!;
    return { id: t.id, target: missionTarget(t, level), carried: 0, done: false };
  });
  // Avoid duplicates when the rotation lands on the same template twice.
  const seen = new Set<string>();
  return {
    level,
    missions: missions.map((m, i) => {
      if (!seen.has(m.id)) return seen.add(m.id), m;
      const spare = MISSION_TEMPLATES.find((t) => !seen.has(t.id)) ?? MISSION_TEMPLATES[i]!;
      seen.add(spare.id);
      return { id: spare.id, target: missionTarget(spare, level), carried: 0, done: false };
    }),
  };
}

function metricValue(stats: RunStats, metric: MissionMetric): number {
  return stats[metric];
}

/** Current progress, including this shift so far. */
export function progressOf(mission: ActiveMission, stats: RunStats | null): number {
  if (mission.done) return mission.target;
  const t = template(mission.id);
  const now = stats ? metricValue(stats, t.metric) : 0;
  return t.scope === 'total' ? mission.carried + now : now;
}

export function missionText(mission: ActiveMission): string {
  const t = template(mission.id);
  const shown = t.metric === 'faresPesewas' ? String(mission.target / 100) : mission.target.toLocaleString();
  return t.text.replace('{n}', shown);
}

/**
 * At the end of a shift: marks finished missions (reported to the server for the reward),
 * carries 'total' progress forward, and levels up once all three are done.
 */
export function settleShift(board: MissionBoard, stats: RunStats): { board: MissionBoard; completed: { id: string; level: number }[] } {
  const completed: { id: string; level: number }[] = [];
  const missions = board.missions.map((mission) => {
    if (mission.done) return mission;
    const progress = progressOf(mission, stats);
    if (progress >= mission.target) {
      completed.push({ id: mission.id, level: board.level });
      return { ...mission, done: true, carried: mission.target };
    }
    return template(mission.id).scope === 'total' ? { ...mission, carried: progress } : mission;
  });
  const allDone = missions.every((m) => m.done);
  return { board: allDone ? newBoard(board.level + 1) : { level: board.level, missions }, completed };
}
